import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import CookieBanner from "@/components/CookieBanner";

// Eager load - critical for initial render
import Index from "./pages/Index";

// Lazy load - secondary pages
const PreventivoWizard = lazy(() => import("./pages/PreventivoWizard"));
const Preventivo = lazy(() => import("./pages/Preventivo"));
const ChiSiamo = lazy(() => import("./pages/ChiSiamo"));
const Servizi = lazy(() => import("./pages/Servizi"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Contatti = lazy(() => import("./pages/Contatti"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <CookieConsentProvider>
          <BrowserRouter>
            <LanguageProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Italian routes (default) */}
                  <Route path="/" element={<Index />} />
                  <Route path="/preventivo" element={<PreventivoWizard />} />
                  <Route path="/preventivo-esperto" element={<Preventivo />} />
                  <Route path="/chi-siamo" element={<ChiSiamo />} />
                  <Route path="/servizi" element={<Servizi />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/contatti" element={<Contatti />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  
                  {/* English routes */}
                  <Route path="/en" element={<Index />} />
                  <Route path="/en/quote" element={<PreventivoWizard />} />
                  <Route path="/en/quote-expert" element={<Preventivo />} />
                  <Route path="/en/about-us" element={<ChiSiamo />} />
                  <Route path="/en/services" element={<Servizi />} />
                  <Route path="/en/portfolio" element={<Portfolio />} />
                  <Route path="/en/contact" element={<Contatti />} />
                  <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/en/cookie-policy" element={<CookiePolicy />} />
                  
                  {/* Admin routes (Italian only) */}
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/change-password" element={<ChangePassword />} />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <CookieBanner />
            </LanguageProvider>
          </BrowserRouter>
        </CookieConsentProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
